import { useRef, useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupIcon from "@mui/icons-material/Group";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { CalendarEvent } from "../../models/calendarEvent";

// Pinnacle Design System tokens — mirrors MonthlyCalendarView
const tokens = {
  primary: "#242C63",
  textPrimary: "#1A2048",
  textSecondary: "#64748B",
  divider: "#D0D4DC",
  accent: "#5D4037",
  font: '"Inter Tight", "Inter", system-ui, sans-serif',
  radiusDialog: "12px",
  shadowDialog: "0px 20px 48px -12px rgba(36, 44, 99, 0.16)",
};

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: "0.75rem",
      fontWeight: 600,
      color: tokens.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      mb: 0.5,
      fontFamily: tokens.font,
    }}
  >
    {children}
  </Typography>
);

const MetaRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", mt: "2px" }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </Box>
  </Box>
);

export interface MeetingDetailsDialogProps {
  open: boolean;
  event: CalendarEvent | null;
  onClose: () => void;
}

const MeetingDetailsDialog = ({
  open,
  event,
  onClose,
}: MeetingDetailsDialogProps) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Reset position whenever the dialog opens or switches to a new event
  useEffect(() => {
    if (open) setPosition({ x: 0, y: 0 });
  }, [open, event?.id, event?.uid]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest("button")) return;
      setDragging(true);
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position],
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging]);

  if (!event) return null;

  const dateStr = event.startDate
    ? dayjs(event.startDate).format("dddd, MMMM D, YYYY")
    : "—";
  const timeStr = event.startDate
    ? `${dayjs(event.startDate).format("HH:mm")}${
        event.endDate ? ` – ${dayjs(event.endDate).format("HH:mm")}` : ""
      }`
    : "—";
  const organizer = event.creatorName || event.creator || "";
  const location = event.location || "";
  const invitees = event.invitees ?? [];

  const valueText = (val: string, fallback?: string) => (
    <Typography
      sx={{
        fontSize: "0.875rem",
        color: val ? tokens.textPrimary : "#C4C9D4",
        fontStyle: val ? "normal" : "italic",
        fontFamily: tokens.font,
        wordBreak: "break-word",
      }}
    >
      {val || fallback}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "escapeKeyDown" || reason === "backdropClick") onClose();
      }}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: tokens.radiusDialog,
          boxShadow: tokens.shadowDialog,
          fontFamily: tokens.font,
          backgroundColor: "#ffffff",
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: dragging ? "none" : undefined,
        },
      }}
    >
      {/* Header — draggable; title + close × */}
      <DialogTitle
        onMouseDown={handleMouseDown}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 3,
          borderBottom: `1px solid ${tokens.divider}`,
          cursor: dragging ? "grabbing" : "grab",
          userSelect: "none",
          gap: 2,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: tokens.primary,
            fontFamily: tokens.font,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {event.name}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t("calendar.detailsClose", { defaultValue: "Close" })}
          sx={{ color: "#64748B", flexShrink: 0 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: "24px !important", pb: 3, px: 3 }}>
        {/* 2-column grid: Date / Time / Organizer / Location */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            rowGap: 2.5,
            columnGap: 3,
          }}
        >
          <MetaRow
            icon={
              <CalendarTodayIcon sx={{ fontSize: 18, color: tokens.accent }} />
            }
            label={t("calendar.detailsDate", { defaultValue: "Date" })}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: tokens.textPrimary,
                fontFamily: tokens.font,
              }}
            >
              {dateStr}
            </Typography>
          </MetaRow>

          <MetaRow
            icon={<AccessTimeIcon sx={{ fontSize: 18, color: tokens.accent }} />}
            label={t("calendar.detailsTime", { defaultValue: "Time" })}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: tokens.textPrimary,
                fontFamily: tokens.font,
              }}
            >
              {timeStr}
            </Typography>
          </MetaRow>

          {organizer ? (
            <MetaRow
              icon={<PersonIcon sx={{ fontSize: 18, color: tokens.accent }} />}
              label={t("calendar.detailsOrganizer", {
                defaultValue: "Organizer",
              })}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: tokens.textPrimary,
                  fontFamily: tokens.font,
                  wordBreak: "break-all",
                }}
              >
                {organizer}
              </Typography>
            </MetaRow>
          ) : (
            <Box /> /* keep grid alignment when organizer absent */
          )}

          <MetaRow
            icon={
              <LocationOnIcon sx={{ fontSize: 18, color: tokens.accent }} />
            }
            label={t("calendar.detailsLocation", { defaultValue: "Location" })}
          >
            {valueText(
              location,
              t("calendar.noLocation", { defaultValue: "No location" }),
            )}
          </MetaRow>
        </Box>

        {/* Participants — only rendered when there are invitees */}
        {invitees.length > 0 && (
          <>
            <Box sx={{ borderTop: `1px solid ${tokens.divider}`, mt: 3 }} />
            <Box sx={{ mt: 2.5 }}>
              <MetaRow
                icon={
                  <GroupIcon sx={{ fontSize: 18, color: tokens.accent }} />
                }
                label={t("calendar.detailsParticipants", {
                  defaultValue: "Participants",
                })}
              >
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}
                >
                  {invitees.map((inv) => (
                    <Chip
                      key={inv}
                      label={inv}
                      size="small"
                      sx={{
                        fontFamily: tokens.font,
                        fontSize: "0.75rem",
                        backgroundColor: "#F1F5F9",
                        color: tokens.textPrimary,
                        height: 26,
                      }}
                    />
                  ))}
                </Box>
              </MetaRow>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MeetingDetailsDialog;
